import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useFragment } from "react-relay";

import { AttendeeCircle } from "@whoosh/design-system/src/components/display";
import { ChargePayerRow } from "@whoosh/design-system/src/components/display/ChargePayerRow";
import type { EventChargePayerRowData } from "@whoosh/design-system/src/components/display/ChargePayerRow/ChargePayerRow";
import {
  Text,
  VStack,
  HStack,
} from "@whoosh/design-system/src/components/primitive";
import { getEventPrimaryChargePaymentData } from "@whoosh/features/src/Events/features/AddorEditSignUp/utils";
import { mergeEligibleWindowCharges } from "@whoosh/features/src/Events/utils/mergeEligibleWindowCharges";

import { currentProfileAtom } from "../../../../features/Club/atoms";
import { groupSizeAtom } from "../../atoms";
import { EventSignUpRow } from "../../types";
import { useCurrentSignUp } from "../useCurrentSignUp/useCurrentSignUp";
import { EventDetailGroupDisplay_event$key } from "./__generated__/EventDetailGroupDisplay_event.graphql";

type EventDetailGroupDisplayProps = {
  fragmentRef: EventDetailGroupDisplay_event$key;
};

export const EventDetailGroupDisplay = ({
  fragmentRef,
}: EventDetailGroupDisplayProps) => {
  const event = useFragment(EventDetailGroupDisplayFragmentString, fragmentRef);

  const currentProfile = useAtomValue(currentProfileAtom);
  const currentMemberId = currentProfile?.memberId;

  const signUp = useCurrentSignUp(event);

  const isGroupEvent =
    event?.maxGuestCount !== 0 ||
    event?.maxMemberCount !== 1 ||
    event?.minMemberCount !== 1 ||
    event?.minGuestCount !== 0;

  // Charges are sourced from the windows the client is eligible for, merged
  // and de-duped across windows (backend already excludes HIDDEN for patrons).
  const primaryRegistrationChargeTypes = useMemo(
    () => mergeEligibleWindowCharges(event.clientEligibleSignUpWindows),
    [event.clientEligibleSignUpWindows]
  );

  const primaryChargeIdToPriceMap = useMemo(() => {
    return primaryRegistrationChargeTypes.flatMap((e) => {
      const curPrice = e?.node?.chargeRuleSet.chargeRules?.edges?.find(
        (cr) => cr?.node?.price
      )?.node?.price;
      const curId = e?.node?.id;
      if (curPrice && curId) {
        return [{ id: curId, price: curPrice }];
      }
      return [];
    });
  }, [primaryRegistrationChargeTypes]);

  const getUnknownGuestsByChargeType = () => {
    const unknownGuestRegistrations =
      currentSignUpRow?.registrations.filter(
        (r) => !r.registrant.playerId && r.type === "GUEST"
      ) || [];

    const summaryMap: Record<string, number> = {};

    unknownGuestRegistrations.forEach((registration) => {
      if (!!summaryMap[registration.primaryRegistrationChargeTypeId ?? ""]) {
        summaryMap[registration.primaryRegistrationChargeTypeId ?? ""]++;
      } else {
        summaryMap[registration.primaryRegistrationChargeTypeId ?? ""] = 1;
      }
    });

    const currentPaymentMap = Object.keys(summaryMap).map((key) => {
      const primaryChargeId = key;
      const total = summaryMap[key];
      return {
        primaryChargeId,
        total,
        primaryChargeName:
          primaryRegistrationChargeTypes.find(
            (n) => n?.node?.id === primaryChargeId
          )?.node?.chargeRuleSet?.displayName ?? "",
        primaryChargePriceInCents:
          primaryRegistrationChargeTypes
            .find((n) => n?.node?.id === primaryChargeId)
            ?.node?.chargeRuleSet?.chargeRules?.edges?.find(
              (cr) => cr?.node?.price
            )?.node?.price ?? 0,
      };
    });

    return currentPaymentMap;
  };

  const currentSignUpRow: EventSignUpRow = {
    id: signUp?.id ?? "",
    waitlistIndex: signUp?.waitlistIndex ?? 0,
    checkedIn: signUp?.checkedIn ?? "NONE",
    registrations:
      signUp?.registrations?.edges?.flatMap((e) =>
        e?.node?.id
          ? [
              {
                id: e.node.id,
                index: e.node.index === undefined ? null : e.node.index,
                type: e.node.type === undefined ? null : e.node.type,
                payingMemberId:
                  e.node.payingMember?.id ?? e.node.registrant?.id ?? "",
                payingCustomerId:
                  e.node.payingCustomer?.id ??
                  e.node.registrant?.id ??
                  (e.node.type === "CUSTOMER" ? signUp?.ownerClient?.id : "") ??
                  "",
                primaryRegistrationChargeTypeId:
                  e.node.primaryChargeType?.id ?? null,
                primaryRegistrationChargeTypeDisplayName:
                  e.node.primaryChargeType?.chargeRuleSet.displayName ?? "",
                primaryRegistrationChargeTypeDisplayDescription:
                  e.node.primaryChargeType?.chargeRuleSet.displayDescription ??
                  "",
                unknownRegistrantDisplayName:
                  e.node.unknownRegistrantDisplayName ?? null,
                registrant: {
                  ...e.node.registrant,
                  playerId: e.node.registrant?.id,
                  phoneNumber:
                    e.node.registrant?.phoneNumber?.nationalNumber ?? null,
                },
              },
            ]
          : []
      ) ?? [],
    ownerClient: signUp?.ownerClient
      ? {
          id: signUp.ownerClient.id ?? "",
          firstName: signUp.ownerClient.firstName ?? "",
          lastName: signUp.ownerClient.lastName ?? "",
          profileImagePublicId: signUp.ownerClient.profileImage?.publicId,
        }
      : undefined,
  };

  const payerData: EventChargePayerRowData[] = getEventPrimaryChargePaymentData(
    {
      currentUserMemberId: currentMemberId ?? null,
      currentSignUpRow,
      primaryChargeIdToPriceMap,
      memberSignUpProxyAllowed: !!event.memberSignUpProxyAllowed,
    }
  );

  const numCustomerRegistrations =
    signUp?.registrations?.edges?.filter(
      (r) => r?.node?.type === "CUSTOMER" && !!r.node.registrant?.id
    ).length ?? 0;

  const numUnknownCustomersRegistrations =
    signUp?.registrations?.edges?.filter(
      (r) => r?.node?.type === "CUSTOMER" && !r.node.registrant?.id
    ).length ?? 0;

  const numMemberRegistrations =
    signUp?.registrations?.edges?.filter((r) => r?.node?.type === "MEMBER")
      .length ?? 0;
  const numGuestRegistrations =
    signUp?.registrations?.edges?.filter((r) => r?.node?.type === "GUEST")
      .length ?? 0;

  const maxGroupSize = useAtomValue(groupSizeAtom);

  const ownerClient = signUp?.ownerClient;

  const supportsPublic = event.eventSeries?.facility?.club?.supportsPublic;

  const getUnknownGuestsForPayer = (payerId: string) => {
    const unknownGuestsByChargeType = getUnknownGuestsByChargeType();
    // Count unknown guests assigned to this payer for this charge type

    return unknownGuestsByChargeType.flatMap((item) => {
      const countAssignedToPayer = currentSignUpRow.registrations.filter(
        (registration) =>
          registration.type === "GUEST" &&
          !registration.registrant.playerId &&
          registration.payingMemberId === payerId &&
          registration.primaryRegistrationChargeTypeId === item.primaryChargeId
      ).length;
      if (
        item.primaryChargeId &&
        item.primaryChargeName &&
        item.primaryChargePriceInCents &&
        item
      ) {
        return {
          primaryChargeName: item.primaryChargeName,
          primaryChargePriceInCents: item.primaryChargePriceInCents,
          primaryChargeId: item.primaryChargeId,
          countAssignedToPayer,
          maximum: item.total,
        };
      }
      return [];
    });
  };

  return (
    <VStack gap={4} pt={{ base: 0, md: 6 }}>
      <HStack justifyContent="space-between" alignItems="center">
        <Text variant="mainSmallSemiBold" py={2}>
          {isGroupEvent ? t`Your Group` : t`Your Signup`}
        </Text>

        {isGroupEvent && maxGroupSize ? (
          <AttendeeCircle
            maxSize={maxGroupSize}
            currentKnownClients={
              supportsPublic ? numCustomerRegistrations : numMemberRegistrations
            }
            currentUnknownClients={
              supportsPublic
                ? numUnknownCustomersRegistrations
                : numGuestRegistrations
            }
            isPublic={supportsPublic ?? false}
          />
        ) : null}
      </HStack>

      <VStack>
        {payerData?.map((payer) => {
          const unknownGuests = getUnknownGuestsForPayer(payer.payer.id);
          return (
            <ChargePayerRow
              {...payer}
              additionalChargeRows={payer.additionalChargeRows}
              key={payer.payer.id}
              shouldShowPayForDropdown={false}
              totalVariant="TOP"
              primaryChargeType={event.primaryChargeType}
              isOwner={payer.payer.id === ownerClient?.id}
              memberSignUpProxyAllowed={!!event.memberSignUpProxyAllowed}
              unknownGuestsOptionsForPayer={unknownGuests}
              isPatronEventDisplay={false}
              isPaymentAssignment={event.primaryChargeType === "PER_SIGN_UP"}
            />
          );
        })}
      </VStack>
    </VStack>
  );
};

const EventDetailGroupDisplayFragmentString = graphql`
  fragment EventDetailGroupDisplay_event on Event {
    ...useCurrentSignUp_event
    id
    maxGuestCount
    maxMemberCount
    minMemberCount
    minGuestCount
    primaryChargeType
    memberSignUpProxyAllowed
    eventSeries {
      facility {
        club {
          supportsPublic
        }
      }
    }
    clientEligibleSignUpWindows(clientId: $memberId) {
      charges {
        edges {
          memberControl
          node {
            id
            chargeRuleSet {
              id
              displayName
              displayDescription
              chargeRules {
                edges {
                  node {
                    id
                    duration
                    price
                    billingId
                    description
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
